import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/tabon/Logo";
import { api, formatError } from "../lib/api";
import { Mail, ArrowLeft, CheckCircle, Phone } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (phone.replace(/\D/g, "").length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
        phone: phone.replace(/\D/g, ""),
      });
      setSent(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 tb-hero-gradient">
      <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md tb-fade-up">
        <div className="flex flex-col items-center mb-8">
          <Logo size="xl" />
          <p className="text-[#A0A0A0] mt-3 text-sm italic">Reset your password</p>
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-7 shadow-2xl">
          {sent ? (
            <div className="flex flex-col items-center gap-4 text-center py-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle size={26} className="text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-1">Check your WhatsApp</h2>
                <p className="text-sm text-[#A0A0A0]">
                  If your details match a registered venue, a reset link has been sent to your registered WhatsApp number. It expires in 60 minutes.
                </p>
              </div>
              <Link
                to="/login"
                className="mt-2 flex items-center gap-2 text-sm text-[#9D00FF] font-semibold hover:underline"
              >
                <ArrowLeft size={14} /> Back to Login
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-1">Forgot Password</h1>
              <p className="text-sm text-[#A0A0A0] mb-6">
                Enter your venue admin email and registered mobile number to receive a reset link via WhatsApp.
              </p>
              <form onSubmit={submit} className="flex flex-col gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
                    <input
                      data-testid="forgot-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@venue.com"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-10 pr-4 py-3 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                    Registered Mobile Number
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
                    <input
                      data-testid="forgot-phone-input"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="off"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      required
                      placeholder="10-digit mobile number"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-10 pr-4 py-3 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                    />
                  </div>
                  <p className="text-[11px] text-[#6B6B6B] mt-1">
                    Enter the mobile number registered with your venue.
                  </p>
                </div>
                {error && (
                  <p className="text-sm text-red-400">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit"
                  className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white font-semibold rounded-lg py-3 min-h-[44px] transition-all"
                >
                  {loading ? "Sending…" : "Send Reset Link"}
                </button>
              </form>
              <div className="mt-5 text-center">
                <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-[#A0A0A0] hover:text-white transition">
                  <ArrowLeft size={14} /> Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
        <div className="text-center text-xs text-[#A0A0A0] mt-6">
          Powered by <span className="text-white">TabOn</span> · tabon.app
        </div>
      </div>
    </div>
  );
}
